import { useEffect, useState } from "react";
import { Alert, Button, Card, Inline, Panel, Stack, StatusBadge } from "@leylion/design-system";
import { downloadReportExport, getReportOccurrences, retryReportDelivery } from "../api/client";
import type { ReportOccurrence } from "../api/reports-types";
import { dateTimeLabel, type ReportsLanguage } from "./reports-components";

const statusAr: Record<string, string> = {
  Pending: "بانتظار التنفيذ", Generating: "جارٍ إنشاء الملف", FileReady: "الملف جاهز",
  Submitted: "قبله خادم البريد", PartiallyFailed: "فشل جزئي", Failed: "فشل", Unknown: "نتيجة غير معروفة — تحتاج مراجعة",
  Blocked: "محجوب", Queued: "بانتظار الإرسال", Sending: "جارٍ الإرسال"
};

function failure(code: string, ar: boolean): string {
  const messages: Record<string, [string, string]> = {
    REPORT_DELIVERY_DISABLED: ["تسليم البريد غير مفعل؛ راجع إعدادات بريد التقارير.", "Report email delivery is disabled; review its configuration."],
    REPORT_RECIPIENT_NOT_APPROVED: ["هذا البريد غير معتمد للاختبار.", "This mailbox is not approved for testing."],
    REPORT_SMTP_CONFIGURATION_INVALID: ["إعدادات بريد التقارير غير مكتملة.", "Report email configuration is incomplete."],
    REPORT_SMTP_TEMPORARY_REJECTION: ["رفض خادم البريد المحاولة مؤقتًا.", "Mail server temporarily rejected this attempt."],
    REPORT_SMTP_PERMANENT_REJECTION: ["رفض خادم البريد التسليم؛ راجع الوجهة والإعدادات.", "Mail server rejected delivery; review destination and settings."],
    REPORT_SMTP_OUTCOME_UNKNOWN: ["قد يكون البريد أُرسل؛ تحقق من الصندوق قبل أي إجراء.", "Mail may have been sent; review the mailbox before taking action."],
    REPORT_SEND_INTERRUPTED: ["انقطع تسجيل نتيجة الإرسال؛ يلزم التحقق من الصندوق.", "Recording the send outcome was interrupted; review the mailbox."],
    REPORT_RECIPIENT_FORBIDDEN: ["المستلم لا يملك صلاحيات بيانات التقرير.", "Recipient lacks access to this report's data."],
    REPORT_ACTOR_INACTIVE: ["الحساب أو العضوية غير فعال.", "Account or membership is inactive."],
    REPORT_ACTOR_FORBIDDEN: ["سُحبت صلاحية الجدولة أو التصدير من المنشئ.", "Creator's schedule or export permission was revoked."],
    REPORT_SCHEDULE_CHANGED: ["تغير الجدول أو توقف؛ راجع إعداداته.", "Schedule changed or paused; review its configuration."],
    REPORT_DEFINITION_CHANGED: ["تغير تعريف التقرير؛ راجع إعداد الجدول.", "Report definition changed; review the schedule."],
    REPORT_RESULT_EXPIRED: ["انتهت صلاحية الملف؛ شغّل تقريرًا جديدًا.", "File expired; generate a new report."]
  };
  const entry = messages[code];
  return entry ? entry[ar ? 0 : 1] : ar ? "تعذر إكمال العملية؛ راجع الصلاحيات والإعدادات وسجل المحاولة." : "Operation could not complete; review permissions, settings and the attempt record.";
}

export function ReportExecutionHistory({ language, refreshKey, canExport }: { language: ReportsLanguage; refreshKey: number; canExport: boolean }) {
  const ar = language === "ar";
  const [rows, setRows] = useState<ReportOccurrence[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => setRows(await getReportOccurrences());
  useEffect(() => { void load().catch(e => setError(String(e.message ?? e))); }, [refreshKey]);
  const act = async (action: () => Promise<unknown>) => {
    setBusy(true); setError("");
    try { await action(); await load(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  return <Panel title={ar ? "سجل تشغيل التقارير وتسليمها" : "Report execution and delivery history"}
    actions={<Button disabled={busy} onClick={() => void act(load)}>{ar ? "تحديث" : "Refresh"}</Button>}>
    <Stack>
      <Alert tone="info" title={ar ? "دليل التسليم" : "Delivery evidence"}>
        {ar ? "قبول خادم البريد لا يثبت الوصول إلى صندوق الوارد. النتيجة غير المعروفة تحتاج مراجعة الصندوق ولا يعاد إرسالها تلقائيًا. رابط التنزيل يحتاج تسجيل الدخول والصلاحيات الحالية."
          : "SMTP acceptance is not inbox confirmation. Unknown outcomes require mailbox review and are not automatically resent. Download links require sign-in and current permissions."}
      </Alert>
      {error && <Alert tone="danger" title={error} />}
      {!rows.length && <span>{ar ? "لا توجد تشغيلات في الفرع الحالي." : "No executions in this branch."}</span>}
      {rows.map(row => <Card key={row.id} title={row.scheduleName}>
        <Stack>
          <Inline><StatusBadge tone={row.status === "Submitted" || row.status === "FileReady" ? "success" : "warning"}>
            {ar ? statusAr[row.status] ?? row.status : row.status}
          </StatusBadge><span>{row.kind === "Manual" ? (ar ? "يدوي — دون إرسال بريد" : "Manual — no email") : (ar ? "تلقائي" : "Automatic")}</span>
            <span>{dateTimeLabel(row.dueAt, language)}</span></Inline>
          {row.skippedOccurrences > 0 && <span>{ar ? `تم تجاوز ${row.skippedOccurrences} موعدًا قديمًا؛ نُفذ آخر موعد فائت فقط.`
            : `${row.skippedOccurrences} older occurrences skipped; only the latest missed occurrence was executed.`}</span>}
          {row.errorCode && <span>{failure(row.errorCode, ar)}</span>}
          {row.exportId && canExport && <Button disabled={busy} onClick={() => void act(() => downloadReportExport(row.exportId!, row.fileName ?? undefined))}>
            {ar ? "تنزيل الملف المحفوظ" : "Download saved file"}</Button>}
          {row.deliveries.map((delivery, index) => <Inline key={delivery.id}>
            <span>{ar ? `المستلم ${index + 1}` : `Recipient ${index + 1}`}: {ar ? statusAr[delivery.status] ?? delivery.status : delivery.status}</span>
            <span>{ar ? `المحاولات: ${delivery.attempts}` : `Attempts: ${delivery.attempts}`}</span>
            {delivery.errorCode && <span>{failure(delivery.errorCode, ar)}</span>}
            {delivery.nextAttemptAt && <span>{dateTimeLabel(delivery.nextAttemptAt, language)}</span>}
            {canExport && ["Failed", "Blocked"].includes(delivery.status) && <Button disabled={busy} variant="secondary"
              onClick={() => void act(() => retryReportDelivery(delivery.id))}>{ar ? "إعادة إرسال الملف نفسه" : "Retry the same file"}</Button>}
          </Inline>)}
        </Stack>
      </Card>)}
    </Stack>
  </Panel>;
}

export function ReportLinkedDownload({ language }: { language: ReportsLanguage }) {
  const id = new URLSearchParams(window.location.search).get("reportExport");
  const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  if (!id || !/^[0-9a-f-]{36}$/i.test(id)) return null;
  const ar = language === "ar";
  return <Panel title={ar ? "التقرير الذي وصلك بالبريد" : "Report from your email"}>
    <Stack>
      <span>{ar ? "اختر المؤسسة والفرع الصحيحين ثم نزّل الملف بصلاحياتك الحالية." : "Select the correct organization and branch, then download with your current permissions."}</span>
      {error && <Alert tone="danger" title={error} />}
      <Button disabled={busy} onClick={() => {
        setBusy(true); setError("");
        void downloadReportExport(id).catch(e => setError(String(e.message ?? e))).finally(() => setBusy(false));
      }}>{ar ? "تنزيل التقرير" : "Download report"}</Button>
    </Stack>
  </Panel>;
}
